import {
  Accordion as AccordionBase,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/common/components/ui/accordion";
import { cn } from "@/common/utils/cn";
import { useRef } from "react";
import { twMerge } from "tailwind-merge";

export interface AccordionsProps {
  items?: {
    index: number;
    id?: string;
    head?: JSX.Element | string;
    body?: JSX.Element | string;
    hide?: boolean;
    disabled?: boolean;
    dataTestid?: string;
  }[];
  accordionsClassName?: string;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  onToggle?: (isOpen: boolean) => void;
  defaultFirstItemOpened?: boolean;
}

const Accordions = ({
  items,
  accordionsClassName,
  className,
  triggerClassName,
  contentClassName,
  onToggle,
  defaultFirstItemOpened,
}: AccordionsProps) => {
  const contentRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const openItemKey = useRef<string | null>(null);

  const defaultOnToggle = (itemKey: string, isOpen: boolean) => {
    const el = contentRefs.current.get(itemKey);
    if (!el) return;
    if (isOpen) {
      el.classList.remove("hidden");
      setTimeout(() => el.classList.add("overflow-visible"), 300);
    } else {
      setTimeout(() => el.classList.add("hidden"), 100);
      el.classList.remove("overflow-visible");
    }
  };

  const handleOnToggle = (itemName: string) => {
    const isOpen = !!itemName;
    if (isOpen) {
      defaultOnToggle(itemName, true);
      openItemKey.current = itemName;
    } else if (openItemKey.current) {
      defaultOnToggle(openItemKey.current, false);
      openItemKey.current = null;
    }
    onToggle && onToggle(isOpen);
  };

  return (
    <AccordionBase
      type="single"
      collapsible
      onValueChange={handleOnToggle}
      defaultValue={defaultFirstItemOpened ? items?.[0]?.id : undefined}
      className={cn("flex flex-col gap-4", accordionsClassName)}
    >
      {items?.map(({ index, id, head, body, hide, disabled, dataTestid }) =>
        hide ? null : disabled ? (
          <div
            key={id || `accordion-${index}`}
            className={twMerge('w-full p-4 rounded-sm bg-white-100', className)}
          >
            <div className="flex min-h-14 items-center py-4">
              {head}
            </div>
          </div>
        ) : (
          <AccordionItem className={twMerge("w-full p-4 rounded-sm bg-white-100", className)} value={id || `accordion-${index}`} key={id || `accordion-${index}`}>
            <AccordionTrigger
              className={twMerge("py-4", triggerClassName)}
              disabled={disabled}
              iconStyles={disabled ? 'hidden' : ''}
              data-testid={dataTestid}
            >
              {head}
            </AccordionTrigger>
          <AccordionContent
              ref={(el) => {
                const key = id || `accordion-${index}`;
                if (el) contentRefs.current.set(key, el);
                else contentRefs.current.delete(key);
              }}
              containerClassName={contentClassName}
            >
              {body}
            </AccordionContent>
          </AccordionItem>
        ))}
    </AccordionBase>
  );
};

export default Accordions;
